/**
 * Диалог «Продлить проживание» (design-guest §7 действие #1).
 * Гость выбирает новую дату выезда → POST /v1/stays/{id}/extensions
 * (требует подтверждения менеджера). Минимум — следующий день после
 * текущего выезда. fee_kopecks не задаём (его проставит менеджер при
 * апруве — гость не торгуется ценой).
 */
import { useState } from 'react';
import { Button } from '@hsc/ui';
import type { StayResponse } from '@hsc/types';
import { formatDate, isoDate } from '../../lib/fleet-format.js';
import { useRequestExtension } from './useGuestStay.js';

export interface ExtendStayDialogProps {
  stay: StayResponse;
  onClose: () => void;
  onDone: (ok: boolean) => void;
}

export function ExtendStayDialog({ stay, onClose, onDone }: ExtendStayDialogProps): JSX.Element {
  const mut = useRequestExtension();
  const minDate = (() => {
    const d = new Date(stay.to);
    d.setDate(d.getDate() + 1);
    return isoDate(d);
  })();
  const [newTo, setNewTo] = useState<string>(minDate);
  const [reason, setReason] = useState('');

  const submit = (): void => {
    if (!newTo) return;
    mut.mutate(
      {
        stayId: stay.id,
        input: {
          new_to_date: new Date(`${newTo}T12:00:00`).toISOString(),
          ...(stay.guest_user_id ? { guest_id: stay.guest_user_id } : {}),
          ...(reason.trim() !== '' ? { reason: reason.trim() } : {}),
        },
      },
      { onSuccess: () => onDone(true), onError: () => onDone(false) },
    );
  };

  return (
    <section className="guest-stay-form" data-testid="extend-dialog">
      <h1 className="hsc-page__title">Продлить проживание</h1>
      <p className="ta-muted">
        Текущий выезд — {formatDate(stay.to)}. Выберите новую дату; менеджер подтвердит наличие
        номера.
      </p>
      <label className="ta-field">
        <span className="ta-field__label">Новая дата выезда</span>
        <input
          type="date"
          className="ta-input"
          min={minDate}
          value={newTo}
          onChange={(e) => setNewTo(e.target.value)}
          data-testid="extend-date"
        />
      </label>
      <label className="ta-field">
        <span className="ta-field__label">Комментарий (необязательно)</span>
        <textarea
          className="ta-input"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Например: рейс перенесли на вечер"
          data-testid="extend-reason"
        />
      </label>
      <div className="room-edit__footer">
        <Button onClick={submit} disabled={mut.isPending || !newTo} data-testid="extend-submit">
          {mut.isPending ? 'Отправляем…' : 'Отправить запрос'}
        </Button>
        <Button variant="secondary" onClick={onClose} data-testid="extend-back">
          Назад
        </Button>
      </div>
    </section>
  );
}
